import { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';

interface CommissionRule {
  id: string;
  name: string;
  min_value: number;
  max_value: number | null;
  percentage: number;
  active: boolean;
}

export default function CommissionCalculator() {
  const [saleValue, setSaleValue] = useState('');
  const [result, setResult] = useState<{
    percentage: number;
    amount: number;
    ruleName: string;
  } | null>(null);
  const [rules, setRules] = useState<CommissionRule[]>([]);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/commissions/rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setRules(data.data.filter((r: CommissionRule) => r.active));
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    }
  };

  const calculateCommission = () => {
    const value = parseFloat(saleValue);
    if (isNaN(value) || value <= 0) {
      alert('Digite um valor válido');
      return;
    }

    // Encontrar regra aplicável
    const applicableRule = rules.find(rule => 
      value >= rule.min_value && 
      (rule.max_value === null || value <= rule.max_value)
    );

    if (applicableRule) {
      const commissionAmount = (value * applicableRule.percentage) / 100;
      setResult({
        percentage: applicableRule.percentage,
        amount: commissionAmount,
        ruleName: applicableRule.name
      });
    } else {
      setResult({
        percentage: 0,
        amount: 0,
        ruleName: 'Nenhuma regra aplicável'
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Valor da Venda (USD)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              step="0.01"
              value={saleValue}
              onChange={(e) => setSaleValue(e.target.value)}
              className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="flex items-end">
          <button
            onClick={calculateCommission}
            className="w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center justify-center gap-2"
          >
            <Calculator className="h-5 w-5" />
            Calcular Comissão
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
          <h3 className="font-semibold text-lg text-gray-900 mb-4">Resultado do Cálculo</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Regra Aplicada</p>
              <p className="text-lg font-bold text-gray-900">{result.ruleName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Percentual</p>
              <p className="text-lg font-bold text-purple-600">{result.percentage}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Valor da Comissão</p>
              <p className="text-2xl font-bold text-green-600">${result.amount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabela de Regras Ativas */}
      <div className="mt-6">
        <h4 className="font-semibold text-gray-900 mb-3">Regras Ativas</h4>
        <div className="bg-gray-50 rounded-lg p-4">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-xs text-gray-600">
                <th className="pb-2">Faixa de Valor</th>
                <th className="pb-2">Percentual</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {rules.length === 0 ? (
                <tr>
                  <td colSpan={2} className="py-2 text-gray-500">Nenhuma regra ativa</td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={rule.id} className="border-t border-gray-200">
                    <td className="py-2">
                      ${rule.min_value.toFixed(2)} - {rule.max_value ? `$${rule.max_value.toFixed(2)}` : 'Ilimitado'}
                    </td>
                    <td className="py-2 font-semibold text-green-600">{rule.percentage}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
